import { Link, useParams, Navigate } from "react-router-dom";
import { Clock, Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../lib/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { getPostBySlug, blogPosts } from "../lib/blogPosts";

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    if (!post) {
        return <Navigate to="/blog" replace />;
    }

    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.description,
        image: post.image,
        datePublished: post.datePublished,
        dateModified: post.datePublished,
        author: { "@type": "Organization", name: post.author },
        publisher: {
            "@type": "Organization",
            name: "Centro de Rehabilitación de Drogas Chile",
            logo: {
                "@type": "ImageObject",
                url: "https://sinadicciones.org/favicon-512x512.png",
            },
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://centroderehabilitaciondedrogas.cl/blog/${post.slug}`,
        },
        keywords: (post.keywords || []).join(", "),
    };

    const faqJsonLd =
        post.faq && post.faq.length > 0
            ? {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: post.faq.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
              }
            : null;

    const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

    return (
        <>
            <Seo
                title={post.title}
                description={post.description}
                path={`/blog/${post.slug}`}
                image={post.image}
                type="article"
                keywords={(post.keywords || []).join(", ")}
                jsonLd={articleJsonLd}
            />
            {faqJsonLd ? (
                <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
            ) : null}

            <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
                <Breadcrumbs
                    items={[
                        { name: "Inicio", path: "/" },
                        { name: "Blog", path: "/blog" },
                        { name: post.title },
                    ]}
                />
                <Link
                    to="/blog"
                    data-testid="blogpost-back"
                    className="mt-5 inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
                >
                    <ArrowLeft className="h-4 w-4" /> Volver al blog
                </Link>

                <header className="mt-6">
                    <h1
                        data-testid="blogpost-h1"
                        className="text-4xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl"
                    >
                        {post.title}
                    </h1>
                    <p className="mt-4 text-lg text-slate-400">{post.description}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(post.datePublished).toLocaleDateString("es-CL", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" /> {post.readingMinutes} min de lectura
                        </span>
                        <span>Por {post.author}</span>
                    </div>
                </header>

                <div className="relative mt-8 aspect-[16/9] overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
                    <img
                        src={post.image}
                        alt={post.title}
                        className="h-full w-full object-cover"
                    />
                </div>

                <div className="mt-10 space-y-8">
                    {post.sections.map((s, i) => (
                        <section key={i}>
                            <h2 className="text-2xl font-bold text-slate-50">{s.heading}</h2>
                            <p className="mt-3 text-base leading-relaxed text-slate-300">
                                {s.body}
                            </p>
                        </section>
                    ))}
                </div>

                {post.faq && post.faq.length > 0 ? (
                    <section className="mt-12">
                        <h2 className="text-2xl font-bold text-slate-50">
                            Preguntas frecuentes
                        </h2>
                        <Accordion
                            type="single"
                            collapsible
                            className="mt-5 rounded-xl border border-slate-800 bg-slate-900"
                            data-testid="blogpost-faq"
                        >
                            {post.faq.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`f-${i}`}
                                    className="border-slate-800 px-5"
                                >
                                    <AccordionTrigger className="text-left text-slate-100 hover:text-cyan-400">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-slate-400">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </section>
                ) : null}

                <aside className="mt-14 rounded-xl border border-cyan-500/30 bg-slate-900 p-6">
                    <h3 className="text-lg font-semibold text-slate-50">
                        ¿Buscas un centro de rehabilitación verificado?
                    </h3>
                    <p className="mt-2 text-sm text-slate-400">
                        Accede a nuestro directorio y filtra por región, modalidad y
                        presupuesto.
                    </p>
                    <Link
                        to="/centros"
                        className="mt-4 inline-flex items-center gap-1 rounded-md bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400"
                    >
                        Ver directorio <ArrowRight className="h-4 w-4" />
                    </Link>
                </aside>

                <section className="mt-14 border-t border-slate-800 pt-10">
                    <h2 className="text-xl font-bold text-slate-50">Seguir leyendo</h2>
                    <div className="mt-5 grid gap-4 md:grid-cols-3">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/blog/${r.slug}`}
                                className="card-hover rounded-xl border border-slate-800 bg-slate-900 p-4"
                            >
                                <h3 className="text-sm font-semibold text-slate-100">
                                    {r.title}
                                </h3>
                                <p className="mt-2 text-xs text-slate-400 line-clamp-2">
                                    {r.description}
                                </p>
                            </Link>
                        ))}
                    </div>
                </section>
            </article>
        </>
    );
}
